import { useState } from "react";
import { toast } from "sonner";
import { Tag, Calendar, Truck, Camera, FileText, Receipt, ArrowRight } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useSEO } from "@/lib/hooks";

export default function ProfessionalsPage() {
  useSEO({ title: "Service dépannage professionnels — Depann7", description: "Service de dépannage et remorquage pour garages, loueurs, VTC, taxis, assurances et concessions en Île-de-France." });
  const [d, setD] = useState({ company_name: "", siret: "", contact_name: "", phone: "", email: "", partner_type: "garage", monthly_volume: "", message: "" });
  const [loading, setLoading] = useState(false);
  const setF = (k) => (e) => setD((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!d.company_name || !d.contact_name || !d.phone || !d.email) { toast.error("Champs obligatoires manquants"); return; }
    setLoading(true);
    try {
      await api.post("/partner-applications", d);
      toast.success("Votre demande a bien été envoyée");
      setD({ company_name: "", siret: "", contact_name: "", phone: "", email: "", partner_type: "garage", monthly_volume: "", message: "" });
    } catch (err) { toast.error(formatErr(err)); } finally { setLoading(false); }
  };

  const inputCls = "w-full h-12 px-4 bg-surface-50 border border-surface-200 rounded-xl text-txt-900 text-sm placeholder:text-txt-300 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all";

  return (
    <div className="bg-white">
      <div className="bg-ink-900 text-white py-14">
        <div className="max-w-6xl mx-auto px-4 lg:px-6">
          <h1 className="text-3xl lg:text-4xl font-extrabold">Professionnels</h1>
          <p className="text-ink-300 mt-2 max-w-2xl">Garages, loueurs, VTC, taxis, assurances, concessions : nous accompagnons vos besoins de transport et de récupération de véhicules.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-12 grid lg:grid-cols-2 gap-10">
        <div className="space-y-4">
          <Block Icon={Tag} title="Tarifs partenaires">Conditions négociées pour les volumes récurrents.</Block>
          <Block Icon={Calendar} title="Facturation mensuelle">Paiement consolidé en fin de mois avec relevé détaillé.</Block>
          <Block Icon={Truck} title="Suivi des missions">Statut en temps réel, GPS, ETA et confirmation de livraison.</Block>
          <Block Icon={Camera} title="Photos avant/après">Document photo systématique pour traçabilité et assurance.</Block>
          <Block Icon={FileText} title="Bon d'intervention">PDF généré automatiquement à la livraison.</Block>
          <Block Icon={Receipt} title="Compte professionnel">Tableau de bord dédié, multi-utilisateurs, exports comptables.</Block>
        </div>

        <form onSubmit={submit} className="bg-white border border-surface-200 rounded-2xl p-6 sm:p-8 shadow-card h-fit" data-testid="partner-form">
          <h2 className="text-xl font-extrabold text-txt-900">Demander un partenariat</h2>
          <p className="text-txt-500 text-sm mt-1">Nous vous recontactons sous 24h</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            <input className={inputCls} placeholder="Société *" value={d.company_name} onChange={setF("company_name")} data-testid="pf-company" />
            <input className={inputCls} placeholder="SIRET" value={d.siret} onChange={setF("siret")} data-testid="pf-siret" />
            <input className={inputCls} placeholder="Contact *" value={d.contact_name} onChange={setF("contact_name")} data-testid="pf-contact" />
            <input className={inputCls} placeholder="Téléphone *" value={d.phone} onChange={setF("phone")} data-testid="pf-phone" />
            <input className={`${inputCls} sm:col-span-2`} placeholder="Email *" type="email" value={d.email} onChange={setF("email")} data-testid="pf-email" />
            <select className={`${inputCls} appearance-none`} value={d.partner_type} onChange={setF("partner_type")} data-testid="pf-type">
              <option value="garage">Garage</option>
              <option value="concession">Concession</option>
              <option value="loueur">Loueur de véhicules</option>
              <option value="vtc">Société VTC</option>
              <option value="taxi">Taxi</option>
              <option value="assurance">Assurance</option>
              <option value="autre">Autre</option>
            </select>
            <input className={inputCls} placeholder="Volume mensuel estimé" value={d.monthly_volume} onChange={setF("monthly_volume")} data-testid="pf-volume" />
            <textarea className={`${inputCls} min-h-[100px] h-auto py-3 resize-y sm:col-span-2`} placeholder="Votre message" value={d.message} onChange={setF("message")} data-testid="pf-message" />
          </div>
          <button disabled={loading} className="mt-5 w-full bg-brand hover:bg-brand-600 text-white font-bold rounded-xl py-4 disabled:opacity-50 transition-colors flex items-center justify-center gap-2" data-testid="pf-submit">
            {loading ? "Envoi..." : "Envoyer ma demande"} <ArrowRight size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

function Block({ Icon, title, children }) {
  return (
    <div className="bg-surface-50 border border-surface-200 rounded-2xl p-5 flex gap-4">
      <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
        <Icon className="text-brand" size={20} />
      </div>
      <div>
        <div className="font-bold text-txt-900">{title}</div>
        <p className="text-txt-500 text-sm mt-1">{children}</p>
      </div>
    </div>
  );
}
